import { dungApiThat, goiApi } from '../api/http';
import { mayChuTenant } from '../../mock/tenant';
import { mockRequest } from '../../mock/transport';
import { useAuthStore } from '../../stores/auth';
import type {
  Tenant,
  TenantCreate,
  TenantDetail,
  TenantDomain,
  TenantDomainApproval,
  TenantPage,
  TenantProvisioning,
  TenantUpdate,
} from '../../modules/tenant/types';

/**
 * Truy cập dữ liệu phân hệ Tenant — bảy endpoint nhóm Tenant của mục 6.2, hai
 * endpoint xác minh domain của ADR-0054 và thêm domain của ADR-0082
 * (`openapi/platform-api.yaml`, tenant-service, G1-D1-2).
 *
 * Mỗi hàm chọn **lúc gọi** giữa API thật và máy chủ giả ở `mock/tenant.ts`
 * (`dungApiThat()`), nên `queryKey`, `staleTime` và việc vô hiệu hoá cache hành
 * xử y hệt ở cả hai chế độ, và view không biết mình đang chạy chế độ nào.
 *
 * Mọi yêu cầu đi qua Gateway ở `/api/v1/...`, không gọi thẳng service (đặc tả
 * Chương 8). Nhóm route này chỉ nhận token `PLATFORM_ADMIN` (ADR-0007).
 */

const GOC = '/api/v1/tenants';

/** Tối đa của `limit` theo spec; trang lớn nhất để ít vòng gọi nhất. */
const MOT_TRANG = 100;

const duongDan = (id: string, hanhDong?: string) =>
  `${GOC}/${encodeURIComponent(id)}${hanhDong ? `/${hanhDong}` : ''}`;

/**
 * Chạy máy chủ giả **sau** nhịp chờ và các trạng thái `?mock=` của
 * `mockRequest`: lỗi nghiệp vụ tới cùng độ trễ với lỗi thật, và `?mock=error`
 * chặn trước khi dữ liệu mẫu bị ghi.
 */
const giaLap = <T>(lam: () => T): Promise<T> => mockRequest(undefined).then(lam);

/**
 * Mọi tenant, nối `next_cursor` tới trang cuối.
 *
 * Tải hết thay vì phân trang theo cursor ở bảng, vì thiết kế cho 50 tenant
 * (ADR-0013) — một trang 100 là đủ — và màn danh sách cần cả tập: ô thống kê
 * đếm theo trạng thái, còn tìm theo tên thì API chưa có. Khi số tenant vượt
 * vài trăm thì chuyển bộ lọc trạng thái, gói sang tham số `status`,
 * `plan_code` của API và phân trang ở server.
 */
async function taiHet(): Promise<Tenant[]> {
  const ra: Tenant[] = [];
  let cursor: string | undefined;
  do {
    const trang = await goiApi<TenantPage>(GOC, { query: { limit: MOT_TRANG, cursor } });
    ra.push(...trang.items);
    cursor = trang.next_cursor ?? undefined;
  } while (cursor);
  return ra;
}

export const tenantRepository = {
  /** `GET /api/v1/tenants` — tenant tạo sau đứng trước, gồm cả tenant đã xoá. */
  danhSach: (): Promise<Tenant[]> =>
    dungApiThat() ? taiHet() : mockRequest(mayChuTenant.danhSach(), []),

  /** `GET /api/v1/tenants/{id}` — kèm domain, người liên hệ, hạn mức, tiến độ provisioning. */
  chiTiet: (id: string): Promise<TenantDetail> =>
    dungApiThat() ? goiApi<TenantDetail>(duongDan(id)) : giaLap(() => mayChuTenant.chiTiet(id)),

  /** `POST /api/v1/tenants` — tạo DRAFT. `khoa` là `Idempotency-Key`, do hook giữ. */
  tao: (du: TenantCreate, khoa: string): Promise<TenantDetail> =>
    dungApiThat()
      ? goiApi<TenantDetail>(GOC, { method: 'POST', body: du, idempotencyKey: khoa })
      : giaLap(() => mayChuTenant.tao(du)),

  /** `PATCH /api/v1/tenants/{id}` — chỉ gửi trường đã đổi. */
  sua: (id: string, du: TenantUpdate): Promise<TenantDetail> =>
    dungApiThat()
      ? goiApi<TenantDetail>(duongDan(id), { method: 'PATCH', body: du })
      : giaLap(() => mayChuTenant.sua(id, du)),

  /**
   * `POST /api/v1/tenants/{id}/provision` — duyệt hồ sơ DRAFT (UC-02).
   *
   * Tenant SAAS trả `202` và sang PROVISIONING kèm `job_id` của lượt onboarding;
   * tenant ON_PREMISE trả `200`, sang thẳng ACTIVE, `job_id` null (ADR-0026 mục 2).
   * Không có `Idempotency-Key`: khoá chống trùng là workflow ID `onboard-{tenant_id}`
   * bên Temporal, nên duyệt hai lần không tạo hai lượt onboarding.
   */
  duyet: (id: string): Promise<TenantProvisioning> =>
    dungApiThat()
      ? goiApi<TenantProvisioning>(duongDan(id, 'provision'), { method: 'POST' })
      : giaLap(() => mayChuTenant.duyet(id)),

  /** `POST /api/v1/tenants/{id}/suspend` — TRIAL, ACTIVE, GRACE_PERIOD → SUSPENDED. */
  tamNgung: (id: string): Promise<TenantDetail> =>
    dungApiThat()
      ? goiApi<TenantDetail>(duongDan(id, 'suspend'), { method: 'POST' })
      : giaLap(() => mayChuTenant.tamNgung(id)),

  /** `POST /api/v1/tenants/{id}/resume` — SUSPENDED → ACTIVE hoặc TRIAL (ADR-0025 mục 2). */
  khoiPhuc: (id: string): Promise<TenantDetail> =>
    dungApiThat()
      ? goiApi<TenantDetail>(duongDan(id, 'resume'), { method: 'POST' })
      : giaLap(() => mayChuTenant.khoiPhuc(id)),

  /**
   * `POST /api/v1/tenants/{id}/domains` — thêm domain chưa xác minh, trả kèm bản
   * ghi TXT cần tạo (ADR-0082). `khoaLap` là `Idempotency-Key`.
   */
  themDomain: (id: string, domain: string, khoaLap: string): Promise<TenantDomain> =>
    dungApiThat()
      ? goiApi<TenantDomain>(duongDan(id, 'domains'), { method: 'POST', idempotencyKey: khoaLap, body: { domain } })
      : giaLap(() => mayChuTenant.themDomain(id, domain)),

  /**
   * `DELETE /api/v1/tenants/{id}/domains/{domain}` — gỡ domain email (GG-FX-39). Tenant bật "Chỉ email thuộc domain đã
   * xác minh" mà gỡ đi thì có người mất đường đăng nhập: `409 TENANT_STATUS_CONFLICT`, câu nói cách xử lý.
   */
  goDomain: (id: string, domain: string): Promise<void> =>
    dungApiThat()
      ? goiApi<void>(duongDan(id, `domains/${encodeURIComponent(domain)}`), { method: 'DELETE' })
      : giaLap(() => mayChuTenant.goDomain(id, domain)),

  /**
   * `POST /api/v1/tenants/{id}/domains/{domain}/check` — tra bản ghi TXT ngay
   * (ADR-0054). `verified_at` vẫn `null` nghĩa là DNS chưa khớp, **không** phải lỗi.
   */
  kiemTraDomain: (id: string, domain: string): Promise<TenantDomain> =>
    dungApiThat()
      ? goiApi<TenantDomain>(duongDan(id, `domains/${encodeURIComponent(domain)}/check`), { method: 'POST' })
      : giaLap(() => mayChuTenant.kiemTraDomain(id, domain)),

  /** `POST /api/v1/tenants/{id}/domains/{domain}/approve` — xác minh thủ công, `ADMIN_APPROVAL` (ADR-0054). */
  xacMinhThuCong: (id: string, domain: string, du: TenantDomainApproval): Promise<TenantDomain> =>
    dungApiThat()
      ? goiApi<TenantDomain>(duongDan(id, `domains/${encodeURIComponent(domain)}/approve`), {
          method: 'POST',
          body: du,
        })
      : giaLap(() =>
          mayChuTenant.xacMinhThuCong(id, domain, du.reason, useAuthStore.getState().user?.id),
        ),
};
